import React, { useCallback, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from 'components';
import { Icon } from 'components/common';
import { icons } from 'assets';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import styles from './VideoSection.module.scss';
import { handleVideoUpload } from 'utilities/Gcp'
// const testVideo = 'https://s3.eu-central-1.amazonaws.com/touche-video/5d8b546b472825002b540c6c.mp4';

interface Props {
  videoUrl?: string;
  readyForEdit?: boolean;
  triggerNewVideoProcessing?: () => void;
  openInEditor?: () => void;
}

const VideoSection = forwardRef((
  {
    videoUrl,
    readyForEdit,
    triggerNewVideoProcessing,
    openInEditor
  }: Props,
  ref
) => {
  // console.log('props', props);
  // const { videoUrl } = props;

  const [video, setVideo] = useState(null);

  useEffect(() => {
    if (!videoUrl) return;

    setVideo({
      src: videoUrl,
      status: 'uploaded'
    });
  }, [videoUrl]);

  const onDrop = useCallback(([currentFile]) => {
    setVideo(v => ({
      ...v,
      status: 'loading'
    }));
    const fileReader = new FileReader();

    fileReader.addEventListener('load', () => {
      const getTheFileName = () => {
        return videoUrl
          ? videoUrl.split('/').pop()
          : `${Number(new Date())}_${Math.random().toString().replace('.', '')}.${currentFile.name.split('.').pop()}`;
      };

      Object.defineProperty(currentFile, 'name', {
        writable: true,
        value: getTheFileName()
      });
      const videoDetails = {
        file: currentFile,
        src: fileReader.result,
        status: 'loaded',
        contentType: currentFile.type
      };
      setVideo(videoDetails);
    }, false);

    fileReader.readAsDataURL(currentFile);
  }, [videoUrl]);

  useImperativeHandle(ref, () => ({
    async handleVideo() {
      if (!video || video.status !== 'loaded') return;
      setVideo(v => ({ ...v, status: 'uploading' }));
      const bucketConfigProps = videoUrl
        ? {
          bucketName: 'touche-video',
          dirName: null,
          region: 'eu-central-1',
        }
        : { dirName: 'videos' };
      const response = await handleVideoUpload(video.file,onProgress => {
        
        setVideo(v => ({ ...v, src: response.location, status: 'uploaded' }));
      });
    },
    getVideoUrl() {
      if (video && video.src) return video.src;
      return null;
    }
  }));

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ['video/mp4']
  });
  const { onClick, ...rootProps } = getRootProps();
  const handleTriggerVideoProcessing = () => {
    setVideo(v => ({
      ...v, status: 'processing'
    }));
    triggerNewVideoProcessing();
  };

  return (
    <div className={styles.container}>

      <label>Video</label>

      <div
        className={isDragActive ? styles.dropzoneActive : styles.dropzone}
        {...rootProps}
      >

        <input {...getInputProps()} />

        <div className={styles.videoContainer}>
          {video &&
            <VideoPlayer src={video.src} key={video.src} />
          }

          {video && video.status === 'processing' &&
            <p>
              <Icon icon={icons.info} />
              Video Processing has been triggered.
            </p>
          }

          {video && video.status === 'loading' &&
            <p>
              <Icon icon={icons.info} />
              Loading...
            </p>
          }

          {video && video.status === 'loaded' &&
            <p>
              <Icon icon={icons.info} />
              The video file will be uploaded when you click "Save Changes".
            </p>
          }

          {video && video.status === 'uploaded' &&
            <p>
              <a href={video.src} target='blank' title={video.src}>Video URL</a>
            </p>
          }
        </div>

        <div className={styles.bttnContainer}>
          {videoUrl &&
            <div>
              video ready for edit:
            <span style={{ color: readyForEdit ? styles.blue : styles.red }}>
                {readyForEdit ? 'YES' : 'NO'}
              </span>
            </div>
          }
          {
            videoUrl
              ? readyForEdit
                ? (
                  <Button
                    type='submit'
                    size='small'
                    onClick={openInEditor}
                    className={styles.bttn}
                  >
                    Open in Editor
                </Button>
                )
                : (
                  <Button
                    type='submit'
                    size='small'
                    onClick={handleTriggerVideoProcessing}
                    className={styles.bttn}
                    disabled={video && video.status === 'processing'}
                  >
                    Trigger New Processing
                </Button>
                )
              : (
                <Button
                  type='add'
                  size='small'
                  onClick={onClick}
                  className={styles.bttn}
                  disabled={video && video.status !== 'loaded'}
                >
                  Browse Files
                </Button>
              )
          }
        </div>

      </div>
    </div>
  );
});

export default VideoSection;
